// src/hooks/useConfirmDialog.ts
import { useState, useCallback } from "react";
import { ConfirmDialogVariant } from "../components/ui/modals/ConfirmDialog";

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
}

interface ConfirmDialogState extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  loading: boolean;
}

export const useConfirmDialog = () => {
  const [state, setState] = useState<ConfirmDialogState>({
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    variant: "info",
    onConfirm: () => {},
    loading: false,
  });

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        setState({
          open: true,
          title: options.title,
          message: options.message,
          confirmText: options.confirmText || "Confirm",
          cancelText: options.cancelText || "Cancel",
          variant: options.variant || "info",
          loading: false,
          onConfirm: () => {
            resolve(true);
            setState((prev) => ({ ...prev, open: false }));
          },
        });

        // Setup cancel handler via timeout to avoid closure issues
        setTimeout(() => {
          const handleCancel = () => {
            resolve(false);
            setState((prev) => ({ ...prev, open: false }));
          };
          setState((prev) => ({ ...prev, onCancel: handleCancel }));
        }, 0);
      });
    },
    []
  );

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, loading }));
  }, []);

  return {
    confirm,
    close,
    setLoading,
    dialogProps: {
      open: state.open,
      onClose: close,
      onConfirm: state.onConfirm,
      title: state.title,
      message: state.message,
      confirmText: state.confirmText,
      cancelText: state.cancelText,
      variant: state.variant,
      loading: state.loading,
    },
  };
};
